export const API_URL =
  process.env.REACT_APP_API_URL || 'http://localhost:4000/api';

export const TOKEN_KEY = 'tabandchords_token';

function isImageSourceString(value) {
  return (
    typeof value === 'string' &&
    (value.startsWith('data:image/') ||
      value.startsWith('http://') ||
      value.startsWith('https://') ||
      value.startsWith('blob:') ||
      (value.startsWith('/') &&
        !looksLikeBase64String(value) &&
        !value.startsWith('/9j/') &&
        !value.startsWith('/9j')))
  );
}

function looksLikeBase64String(value) {
  return (
    typeof value === 'string' &&
    value.length > 100 &&
    !value.includes(' ') &&
    /^[A-Za-z0-9+/=]+$/.test(value)
  );
}

function normalizeImageString(value, fallbackMimeType = 'image/jpeg') {
  if (typeof value !== 'string') {
    return '';
  }

  const trimmedValue = value.trim();

  if (!trimmedValue) {
    return '';
  }

  if (isImageSourceString(trimmedValue)) {
    return trimmedValue;
  }

  if (looksLikeBase64String(trimmedValue)) {
    return `data:${fallbackMimeType};base64,${trimmedValue}`;
  }

  return '';
}

function normalizePageEntry(page) {
  if (typeof page === 'string') {
    return normalizeImageString(page);
  }

  if (!page || typeof page !== 'object') {
    return '';
  }

  const mimeType =
    typeof page.mimeType === 'string' && page.mimeType.startsWith('image/')
      ? page.mimeType
      : typeof page.type === 'string' && page.type.startsWith('image/')
        ? page.type
        : 'image/jpeg';

  const directCandidates = [
    page.src,
    page.url,
    page.dataUrl,
    page.image,
    page.value,
    page.content,
  ];

  for (const candidate of directCandidates) {
    const normalizedCandidate = normalizeImageString(candidate, mimeType);

    if (normalizedCandidate) {
      return normalizedCandidate;
    }
  }

  const dataCandidate =
    typeof page.data === 'string'
      ? page.data
      : typeof page.base64 === 'string'
        ? page.base64
        : '';

  const normalizedDataCandidate = normalizeImageString(dataCandidate, mimeType);

  if (normalizedDataCandidate) {
    return normalizedDataCandidate;
  }

  if (page.type === 'Buffer' && Array.isArray(page.data)) {
    let binaryString = '';

    for (let index = 0; index < page.data.length; index += 0x8000) {
      binaryString += String.fromCharCode(
        ...page.data.slice(index, index + 0x8000)
      );
    }

    const base64String =
      typeof window !== 'undefined' && typeof window.btoa === 'function'
        ? window.btoa(binaryString)
        : '';

    return base64String
      ? `data:${mimeType};base64,${base64String}`
      : '';
  }

  return '';
}

function normalizeSongPages(pages) {
  if (!Array.isArray(pages)) {
    return [];
  }

  return pages.map(normalizePageEntry).filter(Boolean);
}

function normalizeSongPayload(payload = {}) {
  const { pages, ...rest } = payload;

  if (pages === undefined) {
    return rest;
  }

  return {
    ...rest,
    pages: normalizeSongPages(pages),
  };
}

function normalizeSong(song) {
  if (!song || typeof song !== 'object') {
    return song;
  }

  return {
    ...song,
    pages: normalizeSongPages(song.pages),
  };
}

function normalizeSongResponse(response) {
  if (!response || typeof response !== 'object') {
    return response;
  }

  if (response.song) {
    return {
      ...response,
      song: normalizeSong(response.song),
    };
  }

  if (Array.isArray(response.songs)) {
    return {
      ...response,
      songs: response.songs.map(normalizeSong),
    };
  }

  return response;
}

function normalizeIdList(items) {
  if (!Array.isArray(items)) {
    return [];
  }

  return items
    .map((item) => {
      if (typeof item === 'string') {
        return item;
      }

      return item?._id || '';
    })
    .filter(Boolean);
}

function normalizeAlbumCreatePayload(payload = {}) {
  const { songs, songIds, ...rest } = payload;

  if (songIds !== undefined) {
    return {
      ...rest,
      songIds: normalizeIdList(songIds),
    };
  }

  if (songs !== undefined) {
    return {
      ...rest,
      songIds: normalizeIdList(songs),
    };
  }

  return rest;
}

function normalizeAlbumUpdatePayload(payload = {}) {
  const { songs, songIds, ...rest } = payload;

  if (songs !== undefined) {
    return {
      ...rest,
      songs: normalizeIdList(songs),
    };
  }

  if (songIds !== undefined) {
    return {
      ...rest,
      songs: normalizeIdList(songIds),
    };
  }

  return rest;
}

class ApiClient {
  constructor(parent = null, basePath = '') {
    this.parent = parent;
    this.basePath = basePath;
    this.token = '';
    this.onUnauthorized = null;

    if (typeof window !== 'undefined') {
      try {
        this.token = window.localStorage.getItem(TOKEN_KEY) || '';
      } catch {
        this.token = '';
      }
    }
  }

  setToken(token) {
    this.token = token || '';
  }

  setUnauthorizedHandler(handler) {
    this.onUnauthorized = handler || null;
  }

  logout() {
    if (this.parent) return this.parent.logout();
    this.setToken('');
    try {
      window.localStorage.removeItem(TOKEN_KEY);
    } catch {
      // Keep logout usable when browser storage is unavailable.
    }
  }

  headers(includeJson = false) {
    if (this.parent) return this.parent.headers(includeJson);
    return {
      ...(this.token ? { Authorization: `Bearer ${this.token}` } : {}),
      ...(includeJson ? { 'Content-Type': 'application/json' } : {}),
    };
  }

  async request(path, options = {}) {
    if (this.parent) return this.parent.request(`${this.basePath}${path}`, options);
    let response;

    try {
      response = await fetch(`${API_URL}${path}`, options);
    } catch (error) {
      const networkError = new Error(`Unable to reach the API at ${API_URL}`);
      networkError.cause = error;
      throw networkError;
    }

    const contentType = response.headers.get('content-type') || '';
    const isJson = contentType.includes('application/json');
    const data = isJson ? await response.json() : await response.text();

    if (!response.ok) {
      const message =
        isJson && data?.error
          ? data.error
          : typeof data === 'string' && data
            ? data
            : `Request failed with status ${response.status}`;

      const error = new Error(message);
      error.status = response.status;
      error.data = data;

      // Ignore errors from an old session, and let session loading supply its own message.
      const authorization = new Headers(options.headers).get('Authorization');
      if ([401, 403].includes(response.status) && path !== '/session' && this.onUnauthorized &&
          this.token && authorization === `Bearer ${this.token}`) {
        this.onUnauthorized(error);
      }

      throw error;
    }

    return data;
  }

  register(email, password) {
    return this.request('/register', {
      method: 'POST',
      headers: this.headers(true),
      body: JSON.stringify({ email, password }),
    });
  }

  getSession() {
    return this.request('/session', { headers: this.headers(), cache: 'no-store' });
  }

  forBand(bandId) {
    return new ApiClient(this, `/bands/${encodeURIComponent(bandId)}`);
  }

  forUser(userId) {
    return new ApiClient(this, `/users/${encodeURIComponent(userId)}`);
  }

  getBands() {
    return this.request('/bands', { headers: this.headers(), cache: 'no-store' });
  }

  createBand(name) {
    return this.request('/bands', { method: 'POST', headers: this.headers(true), body: JSON.stringify({ name }) });
  }

  getBand(bandId) {
    return this.request(`/bands/${encodeURIComponent(bandId)}`, { headers: this.headers(), cache: 'no-store' });
  }

  renameBand(bandId, name) {
    return this.request(`/bands/${encodeURIComponent(bandId)}`, {
      method: 'PATCH', headers: this.headers(true), body: JSON.stringify({ name }),
    });
  }

  addBandMember(bandId, email) {
    return this.request(`/bands/${encodeURIComponent(bandId)}/members`, {
      method: 'POST', headers: this.headers(true), body: JSON.stringify({ email }),
    });
  }

  removeBandMember(bandId, userId) {
    return this.request(`/bands/${encodeURIComponent(bandId)}/members/${encodeURIComponent(userId)}`, {
      method: 'DELETE', headers: this.headers(),
    });
  }

  getUsers({ q = '', page = 1 } = {}) {
    const params = new URLSearchParams({ q, page: String(page) });
    return this.request(`/users?${params}`, { headers: this.headers(), cache: 'no-store' });
  }

  getSongOwners() {
    return this.request('/users/song-owners', { headers: this.headers(), cache: 'no-store' });
  }

  updateUserRole(userId, role, expectedRole) {
    return this.request(`/users/${encodeURIComponent(userId)}/role`, {
      method: 'PATCH', headers: this.headers(true),
      body: JSON.stringify({ role, expectedRole }),
    });
  }

  updateUserActive(userId, active, expectedActive) {
    return this.request(`/users/${encodeURIComponent(userId)}/active`, {
      method: 'PATCH', headers: this.headers(true),
      body: JSON.stringify({ active, expectedActive }),
    });
  }

  verifyEmail(email, code, password) {
    return this.request('/verify-email', {
      method: 'POST',
      headers: this.headers(true),
      body: JSON.stringify({ email, code, password }),
    });
  }

  resendVerification(email, password) {
    return this.request('/resend-verification', {
      method: 'POST',
      headers: this.headers(true),
      body: JSON.stringify({ email, password }),
    });
  }

  async login(email, password) {
    const data = await this.request('/login', {
      method: 'POST',
      headers: this.headers(true),
      body: JSON.stringify({
        username: email,
        password,
      }),
    });

    return data;
  }

  getSongs() {
    return this.request('/songs', {
      headers: this.headers(),
    }).then(normalizeSongResponse);
  }

  searchSongs(query) {
    const params = new URLSearchParams({ q: query });

    return this.request(`/songs/search?${params.toString()}`, {
      headers: this.headers(),
    }).then(normalizeSongResponse);
  }

  getSong(songId) {
    return this.request(`/getSong/${songId}`, {
      headers: this.headers(),
    }).then(normalizeSongResponse);
  }

  saveSong(payload) {
    return this.request('/saveSong', {
      method: 'POST',
      headers: this.headers(true),
      body: JSON.stringify(normalizeSongPayload(payload)),
    }).then(normalizeSongResponse);
  }

  updateSong(songId, payload) {
    return this.request(`/songs/${songId}`, {
      method: 'PATCH',
      headers: this.headers(true),
      body: JSON.stringify(normalizeSongPayload(payload)),
    }).then(normalizeSongResponse);
  }

  replaceSong(songId, payload) {
    return this.request(`/songs/${songId}`, {
      method: 'PUT',
      headers: this.headers(true),
      body: JSON.stringify(normalizeSongPayload(payload)),
    }).then(normalizeSongResponse);
  }

  deleteSong(songId) {
    return this.request(`/songs/${songId}`, {
      method: 'DELETE',
      headers: this.headers(),
    });
  }

  getAlbums() {
    return this.request('/albums', {
      headers: this.headers(),
    });
  }

  saveAlbum(payload) {
    return this.request('/saveAlbum', {
      method: 'POST',
      headers: this.headers(true),
      body: JSON.stringify(normalizeAlbumCreatePayload(payload)),
    });
  }

  updateAlbum(albumId, payload) {
    return this.request(`/albums/${albumId}`, {
      method: 'PATCH',
      headers: this.headers(true),
      body: JSON.stringify(normalizeAlbumUpdatePayload(payload)),
    });
  }

  replaceAlbum(albumId, payload) {
    return this.request(`/albums/${albumId}`, {
      method: 'PUT',
      headers: this.headers(true),
      body: JSON.stringify(normalizeAlbumUpdatePayload(payload)),
    });
  }

  addSongToAlbum(albumId, songId) {
    return this.request(`/albums/${albumId}/add-song`, {
      method: 'PATCH',
      headers: this.headers(true),
      body: JSON.stringify({ songId }),
    });
  }

  deleteAlbum(albumId) {
    return this.request(`/albums/${albumId}`, {
      method: 'DELETE',
      headers: this.headers(),
    });
  }
}

export const api = new ApiClient();
